import React, { useRef, useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, FileText, Upload, Loader2, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import api from '../../services/api';

// Genel depolama sunucusunun host'u. `AWS:PublicURL` üçünde de (appsettings.json,
// Development.local.json, Production.local.json) AYNI değer — ortama göre değişmeyen sabit
// bir literal, backend'in kendi `StorageDataAccess.VisibilityOf`unun kullandığı önekle birebir
// eşleşir. Yalnız bu host için imzalı çözüm ATLANIR — ürün sahibi kararı: genel adresler
// (avatar, duyuru görseli, sertifika şablonu vb.) bugünkü gibi EKSTRA İSTEK OLMADAN render
// edilmeye devam etmeli. Tanınmayan/eksik bir host İMZALI ÇÖZÜME düşer (backend'in kendi
// VisibilityOf'unun "belirsizse ÖZEL say" ilkesiyle aynı yön): yanlış tarafa düşmenin bedeli
// burada zararsız bir ekstra istektir (backend zaten genel adresleri `/v1/storage/view`den
// İMZASIZ AYNEN döner — bkz. StorageDataAccess.GenerateSignedViewUrl), tersi (özel bir adresi
// genel sanıp hiç çözmemek) ise kalıcı olarak kırık bir görsel üretirdi.
const PUBLIC_STORAGE_HOST = 'storage.flexbee.app';

function isPublicStorageUrl(url) {
  try {
    return new URL(url).hostname === PUBLIC_STORAGE_HOST;
  } catch {
    return false;
  }
}

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinningLoader = styled(Loader2)`
  animation: ${spin} 0.9s linear infinite;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.auxiliar.danger};
  margin-left: 2px;
`;

const FloatingLabel = styled.label`
  position: absolute;
  top: -8px;
  left: 12px;
  z-index: 2;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ $error, theme }) => ($error ? theme.colors.auxiliar.danger : theme.colors.primary.main)};
  background: #fff;
  padding: 0 4px;
  pointer-events: none;
`;

const DropZone = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  width: ${({ $shape }) => ($shape === 'circle' ? '140px' : '100%')};
  height: ${({ $shape }) => ($shape === 'circle' ? '140px' : '160px')};
  border: 2px dashed ${({ $isDragging, $hasError, theme }) =>
    $hasError ? '#E74C3C' : $isDragging ? theme.colors.primary.main : '#E0E4E8'};
  border-radius: ${({ $shape }) => ($shape === 'circle' ? '50%' : '4px')};
  background: ${({ $isDragging }) => ($isDragging ? 'rgba(2, 145, 239, 0.04)' : '#FAFBFC')};
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.2s, background 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.main};
  }
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const PdfPreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PreviewStatus = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: ${({ $error, theme }) => ($error ? '#E74C3C' : '#A0A8B4')};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  text-align: center;
  padding: 0 12px;
`;

const Placeholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #A0A8B4;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  text-align: center;
  padding: 0 12px;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  cursor: pointer;
  z-index: 2;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

const ErrorText = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: #E74C3C;
`;

const SizeHint = styled.span`
  font-size: 11px;
  color: #C0C4C8;
`;

export default function PictureUpload({
  value,
  onChange,
  label,
  error,
  shape = 'square',
  accept = 'image/*',
  validTypes,
  maxSize = 5,
  required = false,
}) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  // 'loading' | 'ready' | 'error' — yalnız `value` KAYITLI bir özel adres olduğunda anlamlı
  // (aşağıdaki efekte bkz.); File örneği ve genel adres için hep senkron 'ready'e geçer, hiçbir
  // zaman gözlemlenebilir bir "loading" karesi göstermezler.
  const [resolveStatus, setResolveStatus] = useState('loading');
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (value instanceof File) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      setResolveStatus('ready');
      return () => URL.revokeObjectURL(url);
    }

    if (typeof value === 'string' && value) {
      if (isPublicStorageUrl(value)) {
        // Genel adres — bugünkü gibi doğrudan render edilir, EKSTRA İSTEK ATILMAZ.
        setPreviewUrl(value);
        setResolveStatus('ready');
        return undefined;
      }

      // Özel adres (ya da tanınmayan host) — önce `/v1/storage/view` ile çöz; ham adres HİÇBİR
      // ZAMAN `previewUrl`e (dolayısıyla `<img src>`e) doğrudan verilmez. DocumentViewerModal'la
      // aynı sözleşme: `.catch()` YOK — api.js interceptor'ı GET okumalarında ASLA reddetmez,
      // gerçek başarı sinyali `res.data?.url`ün varlığıdır (bkz.
      // reference_api_interceptor_never_rejects_reads).
      let cancelled = false;
      setPreviewUrl(null);
      setResolveStatus('loading');
      api.get(`/v1/storage/view?fileUrl=${encodeURIComponent(value)}`).then((res) => {
        if (cancelled) return;
        const signedUrl = res.data?.url;
        if (signedUrl) {
          setPreviewUrl(signedUrl);
          setResolveStatus('ready');
        } else {
          setResolveStatus('error');
        }
      });
      return () => { cancelled = true; };
    }

    setPreviewUrl(null);
    setResolveStatus('loading');
    return undefined;
  }, [value]);

  const isPdf =
    (value instanceof File && value.type === 'application/pdf') ||
    (typeof value === 'string' && value.toLowerCase().endsWith('.pdf'));

  const validate = (file) => {
    if (validTypes && !validTypes.includes(file.type)) {
      setLocalError(t('storage.invalidType'));
      return false;
    }
    if (file.size > maxSize * 1024 * 1024) {
      setLocalError(t('storage.fileTooLarge'));
      return false;
    }
    setLocalError(null);
    return true;
  };

  const handleFile = (file) => {
    if (!file) return;
    if (validate(file)) {
      onChange?.(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    handleFile(file);
    e.target.value = '';
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setLocalError(null);
    onChange?.(null);
  };

  const displayError = localError || error;

  return (
    <Wrapper>
      {label && (
        <FloatingLabel $error={!!displayError}>
          {label}
          {required && <RequiredMark>*</RequiredMark>}
        </FloatingLabel>
      )}
      <DropZone
        $shape={shape}
        $isDragging={isDragging}
        $hasError={!!displayError}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleInputChange}
          style={{ display: 'none' }}
        />

        {value ? (
          <>
            {resolveStatus === 'error' ? (
              <PreviewStatus $error>
                <AlertTriangle size={28} />
                <span>{t('storage.previewUnavailable')}</span>
              </PreviewStatus>
            ) : resolveStatus === 'ready' ? (
              isPdf ? (
                <PdfPreview>
                  <FileText size={36} />
                  <span style={{ fontSize: 12 }}>PDF</span>
                </PdfPreview>
              ) : (
                <PreviewImage src={previewUrl} alt="preview" />
              )
            ) : (
              <PreviewStatus>
                <SpinningLoader size={28} />
                <span>{t('storage.previewLoading')}</span>
              </PreviewStatus>
            )}
            <RemoveButton onClick={handleRemove} title={t('storage.remove')}>
              <X size={14} />
            </RemoveButton>
          </>
        ) : (
          <Placeholder>
            <Upload size={24} />
            <span>{t('storage.dragDrop')}</span>
            <SizeHint>{t('storage.maxSize', { size: maxSize })}</SizeHint>
          </Placeholder>
        )}
      </DropZone>
      {displayError && <ErrorText>{displayError}</ErrorText>}
    </Wrapper>
  );
}
